import { useParams, useSearchParams } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { useAffiliate } from '@/api/affiliate';
import { DetailHeader } from '@/components/detail-header';
import { affiliateTone, humanize } from './status-tones';
import { OverviewTab } from './overview-tab';
import { ClicksTab } from './clicks-tab';
import { ConversionsTab } from './conversions-tab';
import { CommissionsTab } from './commissions-tab';
import { PendingInvoicesTab } from './pending-invoices-tab';
import { PayoutsTab } from './payouts-tab';
import { CommissionPoliciesTab } from './commission-policies-tab';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'conversions', label: 'Referral signups' },
  { value: 'commissions', label: 'Commissions' },
  { value: 'pending-invoices', label: 'Pending invoices' },
  { value: 'payouts', label: 'Payouts' },
  { value: 'policies', label: 'Commission policies' },
] as const;

const TAB_VALUES = TABS.map((t) => t.value) as readonly string[];

/**
 * Affiliate detail page — header (back + #id NAME + status pill) over a
 * 7-tab Tabs switcher persisted in `?tab=`. Mirrors the CustomerDetailPage
 * and ContractDetailPage header idiom.
 */
export default function AffiliateDetailPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam) || 0;
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab');
  const activeTab =
    requestedTab && TAB_VALUES.includes(requestedTab) ? requestedTab : 'overview';

  const { data, isLoading, isError, refetch } = useAffiliate(id);
  const detail = data?.result;

  const onTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Container size="full">
      {isError ? (
        <TabError title="Failed to load affiliate" onRetry={() => refetch()} />
      ) : isLoading || !detail ? (
        <TabLoading rows={4} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <DetailHeader
            parent={{ label: 'Affiliates', to: '/affiliates' }}
            current={detail.referrer_name || detail.referrer_email || `Affiliate #${id}`}
            idPrefix={`#${detail.affiliate_id}`}
            title={detail.referrer_name || detail.referrer_email || `Affiliate #${id}`}
            meta={
              <StatusPill tone={affiliateTone(detail.status)}>
                {humanize(detail.status)}
              </StatusPill>
            }
          />

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={onTabChange}>
            <TabsList>
              {TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="overview">
              <OverviewTab affiliate={detail} />
            </TabsContent>
            <TabsContent value="clicks">
              {activeTab === 'clicks' ? <ClicksTab affiliateId={id} /> : null}
            </TabsContent>
            <TabsContent value="conversions">
              {activeTab === 'conversions' ? <ConversionsTab affiliateId={id} /> : null}
            </TabsContent>
            <TabsContent value="commissions">
              {activeTab === 'commissions' ? <CommissionsTab affiliateId={id} /> : null}
            </TabsContent>
            <TabsContent value="pending-invoices">
              {activeTab === 'pending-invoices' ? <PendingInvoicesTab affiliateId={id} /> : null}
            </TabsContent>
            <TabsContent value="payouts">
              {activeTab === 'payouts' ? <PayoutsTab affiliateId={id} /> : null}
            </TabsContent>
            <TabsContent value="policies">
              {activeTab === 'policies' ? <CommissionPoliciesTab affiliateId={id} /> : null}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </Container>
  );
}
